/**
 * @fileoverview AI Operations dashboard — operational visibility into the AI
 *   layer: AI job execution health, AI API dependency telemetry, AI Foundry
 *   (Azure OpenAI) service health, token-usage cost indicators, and
 *   embedding-pipeline progress.
 * @module system-web/pages/AiOperationsDashboardPage
 *
 * Responsibilities:
 * - Render live AI-layer health from the aiOperationsService via useAiOperations.
 * - Offer a time-window selector and a config-driven "Open in Application
 *   Insights" deep-link for deep investigation.
 *
 * Dependencies:
 * - useAiOperations (aiOperationsService — inherently-online monitoring read).
 * - getWebConfig (portal deep-link URL — never hardcoded).
 *
 * Architecture:
 * - Local-first data-access: this page NEVER calls fetch/apiClient. All data
 *   arrives through the useAiOperations hook that delegates to the service layer.
 *   The AI Operations feed is a recognized inherently-online read (see
 *   aiOperationsService), mirroring the System Status + MongoDB dashboards.
 * Notes:
 * - Every AI-source metric degrades gracefully to "not configured" when the
 *   backend cannot reach Application Insights / Azure Monitor.
 *
 * @component AiOperationsDashboardPage
 * Data Source: local (useAiOperations → aiOperationsService).
 * Constraints: no direct API calls; on-demand snapshot with manual refresh.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles,
  RefreshCw,
  ArrowLeft,
  ExternalLink,
  Cpu,
  Activity,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Coins,
  Layers,
  Zap,
  Clock,
} from 'lucide-react';
import { useAiOperations, AI_WINDOW_OPTIONS, type AiWindowHours } from '../hooks/useAiOperations';
import type {
  AiJobMetrics,
  AiDependencyHealth,
  AiFoundryHealth,
  AiTokenUsage,
  AiEmbeddingProgress,
  AiMetricPoint,
} from '../services/aiOperationsService';
import { getWebConfig } from '../config';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function fmtNum(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}
function fmtDuration(ms: number | null): string {
  if (ms === null) return 'n/a';
  if (ms >= 60_000) return `${(ms / 60_000).toFixed(1)}m`;
  if (ms >= 1_000) return `${(ms / 1_000).toFixed(1)}s`;
  return `${Math.round(ms)}ms`;
}
function timeSince(iso: string | null): string {
  if (!iso) return 'never';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
function StatCard({ label, value, sub, icon: Icon, color }: {
  label: string; value: string; sub?: string; icon: React.ElementType; color?: string;
}) {
  return (
    <div className="flex-1 min-w-[8rem] rounded-lg p-3 border" style={{ borderColor: 'rgba(0,0,0,0.08)', background: 'rgba(0,0,0,0.02)' }}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-3.5 w-3.5 opacity-50" style={color ? { color, opacity: 0.9 } : undefined} />
        <span className="text-xs opacity-60 font-medium uppercase tracking-wide">{label}</span>
      </div>
      <p className="text-xl font-bold tabular-nums" style={color ? { color } : undefined}>{value}</p>
      {sub && <p className="text-xs opacity-50 mt-0.5">{sub}</p>}
    </div>
  );
}

function SectionCard({ title, icon: Icon, children }: {
  title: string; icon: React.ElementType; children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border mb-4 overflow-hidden" style={{ borderColor: 'rgba(0,0,0,0.08)', background: 'var(--card-bg, white)' }}>
      <div className="flex items-center gap-3 px-4 py-3" style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
        <Icon className="h-4 w-4 opacity-60" />
        <span className="font-semibold text-sm flex-1">{title}</span>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function NotConfigured({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-2 text-sm opacity-70">
      <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: '#d97706' }} />
      <span>{message}</span>
    </div>
  );
}

/**
 * @component Sparkline
 * Purpose: Small inline SVG polyline for an AI metric time series.
 * Props:   points (series), color, height, width.
 * Data Source: local (values passed from the hook).
 * Constraints: presentation-only; no data access.
 */
function Sparkline({ points, color = '#8b5cf6', height = 28, width = 140 }: {
  points: AiMetricPoint[]; color?: string; height?: number; width?: number;
}) {
  if (!points || points.length < 2) return <span className="text-xs opacity-40">n/a</span>;
  const values = points.map((p) => p.v);
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const step = width / (points.length - 1);
  const path = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${(i * step).toFixed(1)} ${(height - ((p.v - min) / range) * height).toFixed(1)}`)
    .join(' ');
  return (
    <svg width={width} height={height} className="overflow-visible">
      <path d={path} fill="none" stroke={color} strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Section renderers
// ---------------------------------------------------------------------------
function JobMetricsSection({ metrics }: { metrics: AiJobMetrics | null }) {
  if (!metrics) {
    return (
      <SectionCard title="AI Job Execution" icon={Activity}>
        <NotConfigured message="Not configured — connect Application Insights (AZURE_LOG_ANALYTICS_WORKSPACE_ID) to see AI job execution metrics." />
      </SectionCard>
    );
  }
  return (
    <SectionCard title="AI Job Execution" icon={Activity}>
      <div className="flex flex-wrap gap-3 mb-4">
        <StatCard label="Total Runs" value={fmtNum(metrics.totalRuns)} sub={`last ${metrics.windowHours}h`} icon={Activity} />
        <StatCard
          label="Failure Rate"
          value={`${metrics.overallFailureRatePct}%`}
          icon={metrics.overallFailureRatePct > 0 ? AlertTriangle : CheckCircle}
          color={metrics.overallFailureRatePct >= 10 ? '#dc2626' : metrics.overallFailureRatePct > 0 ? '#d97706' : '#16a34a'}
        />
        <StatCard label="Job Types" value={String(metrics.jobs.length)} icon={Layers} />
      </div>
      {metrics.jobs.length === 0 ? (
        <p className="text-sm opacity-50">No AI job runs in the selected window.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs opacity-50 uppercase tracking-wide text-left">
                <th className="py-2 pr-4 font-medium">Job Type</th>
                <th className="py-2 px-3 font-medium text-right">Completed</th>
                <th className="py-2 px-3 font-medium text-right">Failed</th>
                <th className="py-2 px-3 font-medium text-right">Success</th>
                <th className="py-2 px-3 font-medium text-right">Avg Duration</th>
                <th className="py-2 pl-3 font-medium text-right">Avg Retries</th>
              </tr>
            </thead>
            <tbody>
              {metrics.jobs.map((j) => (
                <tr key={j.jobType} className="border-t" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
                  <td className="py-2 pr-4 font-medium">{j.jobType}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{fmtNum(j.completed)}</td>
                  <td className="py-2 px-3 text-right tabular-nums" style={{ color: j.failed > 0 ? '#dc2626' : undefined }}>{fmtNum(j.failed)}</td>
                  <td className="py-2 px-3 text-right tabular-nums" style={{ color: j.successRatePct >= 95 ? '#16a34a' : j.successRatePct >= 80 ? '#d97706' : '#dc2626' }}>{j.successRatePct}%</td>
                  <td className="py-2 px-3 text-right tabular-nums opacity-70">{fmtDuration(j.avgDurationMs)}</td>
                  <td className="py-2 pl-3 text-right tabular-nums opacity-70">{j.avgRetryCount ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}

function FoundrySection({ foundry }: { foundry: AiFoundryHealth | null }) {
  if (!foundry) {
    return (
      <SectionCard title="AI Foundry (Azure OpenAI)" icon={Cpu}>
        <NotConfigured message="Not configured — no AI service dependency telemetry available for this environment." />
      </SectionCard>
    );
  }
  return (
    <SectionCard title="AI Foundry (Azure OpenAI)" icon={Cpu}>
      {foundry.endpointHost && (
        <p className="text-xs opacity-50 mb-3">Endpoint: <span className="font-mono">{foundry.endpointHost}</span></p>
      )}
      <div className="flex flex-wrap gap-3">
        <StatCard
          label="Availability"
          value={foundry.available ? 'Available' : 'Degraded'}
          icon={foundry.available ? CheckCircle : XCircle}
          color={foundry.available ? '#16a34a' : '#dc2626'}
        />
        <StatCard label="Calls" value={fmtNum(foundry.callCount)} icon={Activity} />
        <StatCard label="Failed" value={fmtNum(foundry.failedCount)} icon={AlertTriangle} color={foundry.failedCount > 0 ? '#dc2626' : undefined} />
        <StatCard label="Avg Latency" value={fmtDuration(foundry.avgLatencyMs)} icon={Clock} />
        <StatCard label="Throttled (429)" value={fmtNum(foundry.throttleCount)} icon={Zap} color={foundry.throttleCount > 0 ? '#d97706' : undefined} />
      </div>
    </SectionCard>
  );
}

function DependencySection({ dep }: { dep: AiDependencyHealth | null }) {
  if (!dep) {
    return (
      <SectionCard title="AI API Dependencies" icon={Zap}>
        <NotConfigured message="Not configured — connect Application Insights to see AI dependency call telemetry." />
      </SectionCard>
    );
  }
  return (
    <SectionCard title="AI API Dependencies" icon={Zap}>
      <div className="flex flex-wrap items-center gap-3">
        <StatCard label="Calls" value={fmtNum(dep.calls)} sub={`last ${dep.windowHours}h`} icon={Activity} />
        <StatCard
          label="Failure Rate"
          value={`${dep.failureRatePct}%`}
          icon={dep.failureRatePct > 0 ? AlertTriangle : CheckCircle}
          color={dep.failureRatePct >= 10 ? '#dc2626' : dep.failureRatePct > 0 ? '#d97706' : '#16a34a'}
        />
        <StatCard label="Avg Duration" value={fmtDuration(dep.avgDurationMs)} icon={Clock} />
        <div className="flex-1 min-w-[10rem] rounded-lg p-3 border" style={{ borderColor: 'rgba(0,0,0,0.08)', background: 'rgba(0,0,0,0.02)' }}>
          <span className="text-xs opacity-60 font-medium uppercase tracking-wide">Call Volume</span>
          <div className="mt-1"><Sparkline points={dep.series} /></div>
        </div>
      </div>
    </SectionCard>
  );
}

function TokenUsageSection({ tokens }: { tokens: AiTokenUsage | null }) {
  if (!tokens) {
    return (
      <SectionCard title="Token Usage (Cost Indicators)" icon={Coins}>
        <NotConfigured message="Not configured — set AZURE_AI_FOUNDRY_RESOURCE_ID (provisioned with AI Foundry diagnostic settings) to see token-usage cost indicators." />
      </SectionCard>
    );
  }
  const series: AiMetricPoint[] = tokens.series.map((p) => ({ t: p.t, v: p.promptTokens + p.generatedTokens }));
  return (
    <SectionCard title="Token Usage (Cost Indicators)" icon={Coins}>
      <div className="flex flex-wrap items-center gap-3">
        <StatCard label="Total Tokens" value={fmtNum(tokens.totalTokens)} sub={`last ${tokens.windowHours}h`} icon={Coins} />
        <StatCard label="Prompt" value={fmtNum(tokens.promptTokens)} icon={ArrowLeft} />
        <StatCard label="Generated" value={fmtNum(tokens.generatedTokens)} icon={Sparkles} />
        <div className="flex-1 min-w-[10rem] rounded-lg p-3 border" style={{ borderColor: 'rgba(0,0,0,0.08)', background: 'rgba(0,0,0,0.02)' }}>
          <span className="text-xs opacity-60 font-medium uppercase tracking-wide">Token Trend</span>
          <div className="mt-1"><Sparkline points={series} color="#0ea5e9" /></div>
        </div>
      </div>
    </SectionCard>
  );
}

function EmbeddingSection({ embedding }: { embedding: AiEmbeddingProgress | null }) {
  if (!embedding) {
    return (
      <SectionCard title="Embedding Pipeline" icon={Layers}>
        <NotConfigured message="Not configured — connect Application Insights to see embedding-pipeline progress." />
      </SectionCard>
    );
  }
  return (
    <SectionCard title="Embedding Pipeline" icon={Layers}>
      {embedding.jobs.length === 0 ? (
        <p className="text-sm opacity-50">No embedding job runs in the selected window.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs opacity-50 uppercase tracking-wide text-left">
                <th className="py-2 pr-4 font-medium">Job Type</th>
                <th className="py-2 px-3 font-medium text-right">Completed</th>
                <th className="py-2 px-3 font-medium text-right">Failed</th>
                <th className="py-2 px-3 font-medium text-right">Avg Duration</th>
                <th className="py-2 pl-3 font-medium text-right">Last Run</th>
              </tr>
            </thead>
            <tbody>
              {embedding.jobs.map((j) => (
                <tr key={j.jobType} className="border-t" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
                  <td className="py-2 pr-4 font-medium">{j.jobType}</td>
                  <td className="py-2 px-3 text-right tabular-nums">{fmtNum(j.completed)}</td>
                  <td className="py-2 px-3 text-right tabular-nums" style={{ color: j.failed > 0 ? '#dc2626' : undefined }}>{fmtNum(j.failed)}</td>
                  <td className="py-2 px-3 text-right tabular-nums opacity-70">{fmtDuration(j.avgDurationMs)}</td>
                  <td className="py-2 pl-3 text-right tabular-nums opacity-70">{timeSince(j.lastRunAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function AiOperationsDashboardPage() {
  const { overview, loading, error, windowHours, setWindowHours, refresh } = useAiOperations();
  const portalUrl = getWebConfig().appInsightsPortalUrl;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="flex-1 min-w-[12rem]">
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Sparkles className="h-5 w-5" style={{ color: '#8b5cf6' }} />
            AI Operations
          </h1>
          <p className="text-xs opacity-50 mt-0.5">AI job execution, dependencies, Foundry health &amp; token usage</p>
        </div>

        <div className="inline-flex rounded-lg border overflow-hidden" style={{ borderColor: 'rgba(0,0,0,0.12)' }}>
          {AI_WINDOW_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setWindowHours(opt.value as AiWindowHours)}
              className="px-3 py-2 text-sm transition-colors"
              style={windowHours === opt.value
                ? { background: '#8b5cf6', color: 'white' }
                : { background: 'transparent' }}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <Link
          to="/system-status"
          className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border transition-colors"
          style={{ borderColor: 'rgba(0,0,0,0.12)' }}
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          System Status
        </Link>

        {portalUrl && (
          <a
            href={portalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border transition-colors"
            style={{ borderColor: 'rgba(0,0,0,0.12)' }}
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Application Insights
          </a>
        )}

        <button
          onClick={refresh}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition-colors"
          style={{ borderColor: 'rgba(0,0,0,0.12)' }}
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {loading && !overview && (
        <div className="p-8 flex items-center justify-center h-40">
          <RefreshCw className="h-6 w-6 animate-spin opacity-40" />
          <span className="ml-3 opacity-60">Loading AI operations…</span>
        </div>
      )}

      {error && !overview && (
        <div className="rounded-xl border p-6 flex items-start gap-3" style={{ borderColor: 'rgba(220,38,38,0.3)', background: 'rgba(220,38,38,0.05)' }}>
          <AlertTriangle className="h-5 w-5 mt-0.5" style={{ color: '#dc2626' }} />
          <div>
            <p className="font-semibold text-sm">Failed to load AI operations</p>
            <p className="text-xs opacity-70 mt-1">{error}</p>
          </div>
          <button onClick={refresh} className="ml-auto text-xs px-3 py-1.5 rounded-lg font-medium" style={{ background: 'rgba(220,38,38,0.1)', color: '#dc2626' }}>Retry</button>
        </div>
      )}

      {overview && (
        <>
          {!overview.configured && (
            <div className="rounded-xl border p-4 mb-4 flex items-start gap-3" style={{ borderColor: 'rgba(217,119,6,0.3)', background: 'rgba(217,119,6,0.05)' }}>
              <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: '#d97706' }} />
              <div>
                <p className="font-semibold text-sm">Application Insights not configured</p>
                <p className="text-xs opacity-70 mt-1">
                  AI telemetry sources require <span className="font-mono">AZURE_LOG_ANALYTICS_WORKSPACE_ID</span> and Monitoring Reader
                  access. Sections below show their real "not configured" state for this environment.
                </p>
              </div>
            </div>
          )}

          <JobMetricsSection metrics={overview.jobMetrics} />
          <FoundrySection foundry={overview.foundry} />
          <DependencySection dep={overview.dependencies} />
          <TokenUsageSection tokens={overview.tokenUsage} />
          <EmbeddingSection embedding={overview.embedding} />

          <p className="text-xs opacity-40 text-right mt-2">
            Updated {timeSince(overview.collectedAt)}
          </p>
        </>
      )}
    </div>
  );
}
